import {
	queryClient,
	SociosSocketEvent,
	type SocketsListener,
	useSocketEventListener,
	withRefreshStrategy
} from '@capsicum/common';
import { useEffect, useMemo, useRef } from 'react';

import type { AuctionBidStatusEnum } from '../../models';

// WS events:
// auction:status:updated - triggered when the auction changes status, usually from enabled to disabled and finished.
// auction:bid:accepted - triggered when there is a new bid accepted in a auction
// auction:bid:outbid - triggered when the user got outbid in a auction
// fanShop:product:updated - triggered when there is a change on a product variant, e.g. title, or description

export const useAuctionProductUpdateFromWS = (
	{ auctionId, productId }: { auctionId: string; productId?: string },
	updateCallback?: (data: { payload: { id: string; status: AuctionBidStatusEnum } }) => void,
	isDismissed = false // dismiss the listeners when the component is unmounted
) => {
	const passedProductId = useRef({});

	useEffect(() => {
		passedProductId.current = productId as string;
	}, [productId]);

	const listeners = useMemo(
		() =>
			new Map<SociosSocketEvent, SocketsListener>([
				[
					SociosSocketEvent.FANSHOP_PRODUCT_UPDATED,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							// BE set 'immediate' refresh strategy, so we need to invalidate queries manually below,
							// for lobby page
							if (passedProductId.current) {
								if (passedProductId.current === data?.payload?.id) {
									queryClient.invalidateQueries({
										queryKey: ['auctionProductUpdateFromWS']
									});
									updateCallback?.(data);
								}
							}
						}
					})
				],
				[
					SociosSocketEvent.AUCTION_STATUS_UPDATED,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							if (auctionId === data?.payload?.id) {
								queryClient.invalidateQueries({
									queryKey: ['auctionProductUpdateFromWS']
								});
								updateCallback?.(data);
							}
						}
					})
				],
				[
					SociosSocketEvent.AUCTION_BID_ACCEPTED,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							if (auctionId === data?.payload?.auction?.id) {
								updateCallback?.(data);
							}
						}
					})
				],
				[
					SociosSocketEvent.AUCTION_BID_OUTBID,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							if (auctionId === data?.payload?.auction?.id) {
								updateCallback?.(data);
							}
						}
					})
				]
			]),
		[auctionId, updateCallback, isDismissed]
	);

	useSocketEventListener(listeners, !!auctionId);
};
